import React, { useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import { UserTypeDBFormat } from '../../constants/securspace-constants';
import { ManagedUser } from './userDisplayUtils';

interface UserActionsMenuProps {
  user: ManagedUser;
  onEdit: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
}

/**
 * Shared "⋮" row-actions menu used by both the desktop table and the mobile cards.
 * Renders Edit for every manageable user; Remove is hidden for the primary account owner.
 */
const UserActionsMenu = ({ user, onEdit, onDelete }: UserActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const canDelete = user.role !== UserTypeDBFormat.ROLE_USERTYPE_PRIMARY_OWNER;

  return (
    <>
      <IconButton
        aria-label="Row actions"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleOpen}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} keepMounted>
        <MenuItem
          onClick={() => {
            handleClose();
            onEdit(user);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>Edit user</ListItemText>
        </MenuItem>
        {canDelete && (
          <MenuItem
            onClick={() => {
              handleClose();
              onDelete(user);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ color: 'error' }}>Remove user</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default UserActionsMenu;
